import { useCallback, useMemo } from 'react';
import type { FocusEventHandler } from 'react';

import Grid from '@mui/material/Grid';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { LegalStatusId } from '@e-socna/data-layer';

import { useLegalStatusesQuery } from '../../hooks/useLegalStatusesQuery.hook';

import { LegalStatusIdAutocomplete } from './LegalStatusIdAutocomplete';

interface Props {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly name?: string;
  readonly onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  readonly onChange: (legalStatusId: string | null) => void;
  readonly required?: boolean;
  readonly value: LegalStatusId | null;
}

export const LegalStatusFields = ({
  disabled = false,
  error = false,
  helperText,
  name,
  onBlur,
  onChange,
  required = false,
  value,
}: Readonly<Props>) => {
  const { data, isLoading } = useLegalStatusesQuery();

  const legalStatuses = data?.findAllActiveLegalStatuses ?? EMPTY_ARRAY;

  const legalStatusById = useMemo(
    () =>
      new Map(
        legalStatuses.map((legalStatus) => [legalStatus._id, legalStatus]),
      ),
    [legalStatuses],
  );

  const selectedLegalStatus = value ? legalStatusById.get(value) : undefined;
  const groupId = selectedLegalStatus
    ? (selectedLegalStatus.parentId ?? selectedLegalStatus._id)
    : null;
  const childId = selectedLegalStatus?.parentId
    ? selectedLegalStatus._id
    : null;

  const groupOptions = useMemo(
    () => legalStatuses.filter((legalStatus) => !legalStatus.parentId),
    [legalStatuses],
  );

  const childOptions = useMemo(
    () =>
      legalStatuses.filter(
        (legalStatus) =>
          !!legalStatus.parentId &&
          (!groupId || legalStatus.parentId === groupId),
      ),
    [legalStatuses, groupId],
  );

  const handleGroupChange = useCallback(
    (newGroupId: string | null) => {
      onChange(newGroupId);
    },
    [onChange],
  );

  const handleChildChange = useCallback(
    (newChildId: string | null) => {
      onChange(newChildId ?? groupId);
    },
    [groupId, onChange],
  );

  return (
    <>
      <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
        <LegalStatusIdAutocomplete
          disabled={disabled}
          label="Groupe de statut juridique"
          loading={isLoading}
          noOptionsText="Aucun groupe de statut juridique trouvé"
          onChange={handleGroupChange}
          options={groupOptions}
          value={groupId}
        />
      </Grid>
      <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
        <LegalStatusIdAutocomplete
          disabled={disabled}
          error={error}
          helperText={helperText}
          label="Statut juridique"
          loading={isLoading}
          name={name}
          onBlur={onBlur}
          onChange={handleChildChange}
          options={childOptions}
          required={required}
          value={childId}
        />
      </Grid>
    </>
  );
};
